/**
 * coverageStore — derives Coverage-tab data from sandbox persistence
 * files written by the design-graph runtime (Phase 3).
 *
 * Pure derivation: same input files → same output. No side effects,
 * no localStorage. Components call `parseCoverageFromFiles(files)`
 * to get a snapshot of:
 *
 *   - the review queue (items awaiting human resolution)
 *   - graph stats (node/edge counts by kind)
 *   - calibration health (recent ECE / sample count)
 *
 * This file is the inverse of `RuntimePersistence.serialize…()` —
 * we read the same JSON/JSONL formats. Schema v1.
 *
 * Game-agnostic: the parsers don't interpret content; they just shape
 * it for display.
 */

// ─── Wire-format constants (mirrors RuntimePersistence.ts) ──────────

const RECONCILER_DIR = '/game-design/.reconciler/';
export const PATH_GRAPH_SNAPSHOT = `${RECONCILER_DIR}graph-snapshot.json`;
export const PATH_CALIBRATION_LOG = `${RECONCILER_DIR}calibration-log.jsonl`;
export const PATH_REVIEW_QUEUE = `${RECONCILER_DIR}review-queue.json`;

const SCHEMA_VERSION = 1;

// ─── Types we EXPOSE to the UI ──────────────────────────────────────

export type ReviewReason =
  | 'low-confidence'
  | 'verifier-failed'
  | 'no-confidence'
  | 'post-validation-failed';

export interface CoverageReviewItem {
  id: string;
  reason: ReviewReason;
  detail?: string;
  sourceNodeIds: string[];
  addedAt: string;
  /** Verdict kind (`keep`/`revise`/`drop`/`add`) for badge display. */
  verdictKind: string;
  /** Edge id when applicable; `from→to` for `add`. */
  verdictTarget: string;
  /** Verdict rationale, surfaced to the operator. */
  rationale: string;
}

export interface CoverageGraphStats {
  /** Total node count. */
  nodes: number;
  /** Total edge count. */
  edges: number;
  /** Per-edge-source breakdown. */
  edgesBySource: { manual: number; derived: number; llmDerived: number };
  /** Per-edge-kind breakdown for visibility into graph density. */
  edgesByKind: Record<string, number>;
}

export interface CoverageCalibrationStats {
  /** Total decision-time entries. */
  decisions: number;
  /** Total resolution entries (humanVerdict set). */
  resolutions: number;
  /** Mean confidence across all decisions, in [0, 1]. */
  meanConfidence: number;
  /** Mean confidence among resolutions for which humanVerdict='correct'. */
  meanConfidenceCorrect: number | null;
  /** Mean confidence among resolutions for which humanVerdict='incorrect'. */
  meanConfidenceIncorrect: number | null;
}

export interface CoverageData {
  reviewQueue: CoverageReviewItem[];
  graphStats: CoverageGraphStats;
  calibrationStats: CoverageCalibrationStats;
  /** Diagnostic warnings (corrupt artifact, version mismatch, etc.). */
  warnings: string[];
  /** True when no reconciler artifacts were present in the files map. */
  empty: boolean;
}

// ─── Top-level parser ───────────────────────────────────────────────

export function parseCoverageFromFiles(files: Map<string, string>): CoverageData {
  const warnings: string[] = [];
  const queueRaw = files.get(PATH_REVIEW_QUEUE);
  const snapRaw = files.get(PATH_GRAPH_SNAPSHOT);
  const logRaw = files.get(PATH_CALIBRATION_LOG);

  const reviewQueue = parseReviewQueue(queueRaw, warnings);
  const graphStats = parseGraphStats(snapRaw, warnings);
  const calibrationStats = parseCalibrationStats(logRaw, warnings);

  const empty = queueRaw === undefined && snapRaw === undefined && logRaw === undefined;

  return { reviewQueue, graphStats, calibrationStats, warnings, empty };
}

// ─── Review queue ───────────────────────────────────────────────────

function parseReviewQueue(raw: string | undefined, warnings: string[]): CoverageReviewItem[] {
  if (raw === undefined || raw.length === 0) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    warnings.push(`review queue is not valid JSON: ${(err as Error).message}`);
    return [];
  }
  if (!isObj(parsed) || !Array.isArray(parsed['items'])) {
    warnings.push('review queue root is not the expected { items: [...] } shape');
    return [];
  }

  const items: CoverageReviewItem[] = [];
  for (const raw of parsed['items']) {
    const item = decodeReviewItem(raw);
    if (item) items.push(item);
  }
  return items;
}

function decodeReviewItem(raw: unknown): CoverageReviewItem | null {
  if (!isObj(raw)) return null;
  if (typeof raw['id'] !== 'string') return null;
  if (typeof raw['reason'] !== 'string') return null;
  if (typeof raw['addedAt'] !== 'string') return null;
  if (!Array.isArray(raw['sourceNodeIds'])) return null;
  const verdict = raw['verdict'];
  if (!isObj(verdict) || typeof verdict['kind'] !== 'string') return null;

  const verdictKind = verdict['kind'];
  let verdictTarget = '';
  if (verdictKind === 'keep' || verdictKind === 'revise' || verdictKind === 'drop') {
    verdictTarget = typeof verdict['edgeId'] === 'string' ? verdict['edgeId'] : '';
  } else if (verdictKind === 'add') {
    const from = typeof verdict['from'] === 'string' ? verdict['from'] : '?';
    const to = typeof verdict['to'] === 'string' ? verdict['to'] : '?';
    verdictTarget = `${from} → ${to}`;
  }

  return {
    id: raw['id'],
    reason: raw['reason'] as ReviewReason,
    addedAt: raw['addedAt'],
    sourceNodeIds: raw['sourceNodeIds'].filter((x): x is string => typeof x === 'string'),
    verdictKind,
    verdictTarget,
    rationale: typeof verdict['rationale'] === 'string' ? verdict['rationale'] : '',
    ...(typeof raw['detail'] === 'string' ? { detail: raw['detail'] } : {}),
  };
}

// ─── Graph snapshot stats ───────────────────────────────────────────

function parseGraphStats(raw: string | undefined, warnings: string[]): CoverageGraphStats {
  const empty: CoverageGraphStats = {
    nodes: 0, edges: 0,
    edgesBySource: { manual: 0, derived: 0, llmDerived: 0 },
    edgesByKind: {},
  };
  if (raw === undefined || raw.length === 0) return empty;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    warnings.push(`graph snapshot is not valid JSON: ${(err as Error).message}`);
    return empty;
  }
  if (!isObj(parsed)) {
    warnings.push('graph snapshot root is not an object');
    return empty;
  }
  if (typeof parsed['version'] !== 'number') {
    warnings.push('graph snapshot missing version field');
    return empty;
  }
  if (parsed['version'] > SCHEMA_VERSION) {
    warnings.push(`graph snapshot version ${parsed['version']} newer than UI supports (${SCHEMA_VERSION})`);
    return empty;
  }

  const nodes = Array.isArray(parsed['nodes']) ? parsed['nodes'].length : 0;
  const edgesArr = Array.isArray(parsed['edges']) ? parsed['edges'] : [];
  const edges = edgesArr.length;

  const edgesBySource = { manual: 0, derived: 0, llmDerived: 0 };
  const edgesByKind: Record<string, number> = {};
  for (const e of edgesArr) {
    if (!isObj(e)) continue;
    const src = e['source'];
    if (src === 'manual') edgesBySource.manual += 1;
    else if (src === 'derived') edgesBySource.derived += 1;
    else if (src === 'llm-derived') edgesBySource.llmDerived += 1;
    const kind = typeof e['kind'] === 'string' ? e['kind'] : 'unknown';
    edgesByKind[kind] = (edgesByKind[kind] ?? 0) + 1;
  }

  return { nodes, edges, edgesBySource, edgesByKind };
}

// ─── Calibration log stats ──────────────────────────────────────────

function parseCalibrationStats(raw: string | undefined, warnings: string[]): CoverageCalibrationStats {
  const empty: CoverageCalibrationStats = {
    decisions: 0,
    resolutions: 0,
    meanConfidence: 0,
    meanConfidenceCorrect: null,
    meanConfidenceIncorrect: null,
  };
  if (raw === undefined || raw.length === 0) return empty;

  let decisions = 0;
  let resolutions = 0;
  let confSum = 0;
  let confCorrectSum = 0, confCorrectCount = 0;
  let confIncorrectSum = 0, confIncorrectCount = 0;

  for (const line of raw.split('\n')) {
    if (line.length === 0) continue;
    let entry: unknown;
    try { entry = JSON.parse(line); } catch { continue; }
    if (!isObj(entry)) continue;
    if (typeof entry['predictedConfidence'] !== 'number') continue;
    const conf = entry['predictedConfidence'];
    const verdict = entry['humanVerdict'];

    if (verdict === undefined) {
      decisions += 1;
      confSum += conf;
    } else {
      resolutions += 1;
      if (verdict === 'correct') {
        confCorrectSum += conf;
        confCorrectCount += 1;
      } else if (verdict === 'incorrect') {
        confIncorrectSum += conf;
        confIncorrectCount += 1;
      }
    }
  }

  // Suppress unused warning channel — corrupt entries already silently
  // skipped; they're not error-worthy at this granularity.
  void warnings;

  return {
    decisions,
    resolutions,
    meanConfidence: decisions > 0 ? confSum / decisions : 0,
    meanConfidenceCorrect: confCorrectCount > 0 ? confCorrectSum / confCorrectCount : null,
    meanConfidenceIncorrect: confIncorrectCount > 0 ? confIncorrectSum / confIncorrectCount : null,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
